import { renderStoreBadges, renderLanguagePicker } from './ui.mjs';
import { formatCurrency, formatDate } from './utils.mjs';
import { updateHeader } from './main.mjs';
import { STORE_LINKS } from './config.mjs';
import router from './router.mjs';
import t from 'virtual:i18n';

/**
 * Curated purple, teal, and cyan palette — ported directly from the Flutter app's
 * `getColorFromHash()` in shared_ui/lib/utils/utils.dart.
 * Same hash algorithm, same colors, same white text → avatars match the mobile app.
 */
const FLUTTER_AVATAR_COLORS = [
    '#20B2AA', // Light Sea Green (Teal)
    '#48D1CC', // Medium Turquoise
    '#DDA0DD', // Plum
    '#DA70D6', // Orchid
    '#BA55D3', // Medium Orchid
    '#9370DB', // Medium Purple
    '#5F9EA0', // Cadet Blue (Teal)
    '#9B87F5', // Soft Purple
    '#00CED1', // Dark Turquoise
    '#40E0D0', // Turquoise
    '#AB82C5', // Amethyst
    '#9F7AEA', // Purple Haze
];

/**
 * Returns a consistent color for a given string, mirroring getColorFromHash() from Flutter.
 */
function getAvatarColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return FLUTTER_AVATAR_COLORS[Math.abs(hash) % FLUTTER_AVATAR_COLORS.length];
}

/**
 * Render the Home screen with list of groups as clickable tiles.
 * Each tile shows: name, last updated, number of expenses, and total amount.
 * Sources groups from localStorage saved by fetchGroup().
 */
export function renderHome() {
    const el = document.getElementById('app-container');
    if (!el) return;

    const LS_GROUPS_KEY = 'splitpal:groups';

    // Reset Header using centralized logic
    updateHeader(null);

    let groups = [];
    try {
        const raw = localStorage.getItem(LS_GROUPS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        groups = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        groups = [];
    }

    if (!groups || groups.length === 0) {
        renderWelcomePage(el);
        return;
    }

    // Clear the container before rendering groups
    el.innerHTML = '';

    const container = document.createElement('div');
    container.id = 'home-groups-container';

    // Sort groups by updatedAt desc for nicer UX
    groups.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

    // Build tiles
    groups.forEach((group) => {
        const name = group.name || 'Untitled Group';
        const currency = group.currency || '';
        const totalExpenses = Array.isArray(group.expenses) ? group.expenses.length : 0;
        const totalAmount = group.settlement && typeof group.settlement.totalSpent === 'number' ? group.settlement.totalSpent : (Array.isArray(group.expenses) ? group.expenses.reduce((sum, e) => sum + (e.amount || 0), 0) : 0);
        const updatedAt = group.updatedAt;
        const lastUpdated = updatedAt ? new Date(updatedAt).toLocaleString() : '—';

        const cardEl = document.createElement('div');
        cardEl.className = 'list-item';

        const dateStr = updatedAt ? formatDate(updatedAt) : '—';
        const amountStr = formatCurrency(totalAmount || 0, currency);
        const receiptsText = `${totalExpenses} ${totalExpenses === 1 ? t.receiptCount : t.receiptsCount}`;

        const avatarColor = getAvatarColor(name);
        const avatarLetter = name.charAt(0).toUpperCase();

        cardEl.innerHTML = `
            <div class="list-item-leading">
                <div class="tab-avatar" style="background-color:${avatarColor};">${avatarLetter}</div>
            </div>
            <div class="list-item-content">
                <div class="list-item-title">${name}</div>
                <div class="list-item-subtitle">${dateStr} &middot; ${receiptsText}</div>
            </div>
            <div class="list-item-trailing">
                <span class="list-item-amount-large">${amountStr}</span>
                <div class="list-item-goto">›</div>
            </div>
        `;

        cardEl.onclick = () => {
            if (group.id) {
                router.navigate(`/home/${group.id}`);
            }
        };

        container.appendChild(cardEl);
    });

    el.appendChild(container);

    // Add FAB button for creating new tabs (removed in favor of header button)
    // renderFAB(el);
}



/**
 * Renders the FAB (Floating Action Button) for creating new tabs
 * @param {HTMLElement} parentEl - Parent element to append to
 */
async function renderFAB(parentEl) {
    const { createFAB } = await import('./components.mjs');

    const plusIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>`;

    // createFAB attaches translateX-based hover handlers we don't want.
    // Clone the node immediately to strip all those listeners cleanly.
    const rawFab = createFAB({ icon: plusIcon });
    const fab = rawFab.cloneNode(true);

    // Circular, bottom-right positioning
    Object.assign(fab.style, {
        position: 'fixed',
        bottom: '30px',
        left: 'auto',
        right: '24px',
        transform: 'none',
        width: '56px',
        height: '56px',
        padding: '0',
        borderRadius: '50%',
        justifyContent: 'center',
        backgroundColor: 'var(--color-primary)',
        color: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        cursor: 'pointer',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        zIndex: '1000',
        transition: 'box-shadow 0.2s ease',
    });

    // Clean hover — only shadow changes, no movement
    fab.addEventListener('mouseenter', () => {
        fab.style.boxShadow = '0 6px 20px rgba(var(--color-primary-rgb), 0.5)';
    });
    fab.addEventListener('mouseleave', () => {
        fab.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    });
    fab.addEventListener('mousedown', () => {
        fab.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
    });
    fab.addEventListener('mouseup', () => {
        fab.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    });

    fab.addEventListener('click', async () => {
        const { showCreateTabModal } = await import('./createTab.mjs');
        showCreateTabModal();
    });

    parentEl.appendChild(fab);
}

/**
 * Renders a simple, elegant welcome page when no groups exist
 * @param {HTMLElement} parentEl - Parent element to append to
 */
async function renderWelcomePage(parentEl) {
    parentEl.innerHTML = '';

    const welcomeContainer = document.createElement('div');
    welcomeContainer.className = 'welcome-container';

    welcomeContainer.innerHTML = `
        <!-- Language Picker -->
        <div id="welcome-lang-picker" style="margin-bottom: 24px; display: flex; justify-content: center;"></div>

        <!-- Welcome Message -->
        <h1 class="welcome-heading">${t.welcomeHeading}</h1>
        <p class="welcome-description">
            ${t.welcomeDescription}
        </p>
        
        <!-- CTA Button -->
        <button class="welcome-button" id="start-splitting-btn">
            ${t.startSplitting}
        </button>
    
    `;

    parentEl.appendChild(welcomeContainer);

    // Render Language Picker
    const langPickerContainer = welcomeContainer.querySelector('#welcome-lang-picker');
    if (langPickerContainer) {
        renderLanguagePicker(langPickerContainer);
    }

    // Add store badges
    const storeBadgesContainer = welcomeContainer.querySelector('#welcome-store-badges');
    if (storeBadgesContainer) {
        renderStoreBadges(storeBadgesContainer);
    }

    // Attach event listener to the CTA button
    const ctaButton = document.getElementById('start-splitting-btn');
    if (ctaButton) {
        ctaButton.addEventListener('click', async () => {
            const { showCreateTabModal } = await import('./createTab.mjs');
            showCreateTabModal();
        });
    }
}
